import styled from 'styled-components';
import { spacing, colors } from 'ui/theme';
import { SECONDS_IN_DAY, SECONDS_IN_HOUR, SECONDS_IN_MINUTE } from './constants';
import { formatTime, pluralize } from './utils';

const StyledDiv = styled.div`
  margin-top: ${spacing.base};
  color: ${colors.textSecondary};
`;

export const SEND_TIME_ESTIMATE_TEST_ID = 'sendTimeEstimate';

/**
 * Granularity for the time estimate calculation
 * - Less than 1 min
 * When this campaign sends, it will take about 1 minute to complete.
 * - 1 min - 59 mins (round up to the nearest minute)
 * When this campaign sends, it will take at least [X] minutes to complete.
 * - 1 hour + (round up to the nearest minute)
 * When this campaign sends, it will take at least 4 hours to complete.
 * When this campaign sends, it will take at least 4 hours and 33 minutes to complete.
 * When this campaign sends, it will take at least 1 day, 4 hours, and 33 minutes to complete.
 *
 * See angular implementation in main repo:
 * @see https://github.com/Iterable/iterable/blob/72ee44003f2cd5bb52ee26bb3e4c605dfa8061ce/public/es6/app/directives/sendTimeEstimate.js
 * @see https://github.com/Iterable/iterable/blob/38053194f684b1c889324548c5b36b2f09e423e3/app/views/campaigns/reviewCampaign.scala.html
 */
export const SendTimeEstimate = ({ size, rateLimit: { rateLimitUnit, rateLimitValue } }: any) => {
  let rateLimitPerMin;

  // convert if necessary
  if (rateLimitUnit === 'minute') {
    rateLimitPerMin = rateLimitValue;
  } else if (rateLimitUnit === 'second') {
    rateLimitPerMin = rateLimitValue * 60;
  }

  const timeParts = [];
  const rateLimitPerSecond = rateLimitPerMin / SECONDS_IN_MINUTE;
  const durationInSeconds = Math.ceil(size / rateLimitPerSecond);
  const wholeDays = Math.floor(durationInSeconds / SECONDS_IN_DAY);
  const wholeHours = Math.floor((durationInSeconds % SECONDS_IN_DAY) / SECONDS_IN_HOUR);
  const wholeMinutes = Math.floor((durationInSeconds % SECONDS_IN_HOUR) / SECONDS_IN_MINUTE);

  if (wholeDays > 0) {
    timeParts.push(pluralize(wholeDays, 'day'));
  }
  if (wholeHours > 0) {
    timeParts.push(pluralize(wholeHours, 'hour'));
  }
  if (wholeMinutes > 0) {
    timeParts.push(pluralize(wholeMinutes, 'minute'));
  }

  const aboutOrAtLeast = durationInSeconds < SECONDS_IN_MINUTE ? 'about 1 minute' : 'at least';
  const duration = formatTime(timeParts);

  return (
    <StyledDiv data-test={SEND_TIME_ESTIMATE_TEST_ID}>
      When this campaign sends, it will take{' '}
      <b>
        {aboutOrAtLeast} {duration}
      </b>{' '}
      to complete.
    </StyledDiv>
  );
};
